import { model as routerModel } from '!/router'
import { combine, createEvent, sample } from 'effector'
import { model as guestModel } from '~/features/subscriber/guest'
import { aye, nay } from '~/shared/helpers'
import type { TryIsPurchased } from '../ExternalPaymentOpen/index.h'
import { giveItATry } from '../ExternalPaymentOpen/model'
import { $isExternallyPaid } from '../model'
import type { GenPlayerPaymentLogicParams, PaymentItem } from './index.h'
import { $paymentButtonTranslation } from './translations'

export const genPlayerPaymentLogic = <T extends PaymentItem>({
  entityStore,
  paymentEvent,
  tvShow,
  shouldRefresh,
}: GenPlayerPaymentLogicParams<T>) => {
  const openPlayerPaymentPopup = createEvent()

  const $isExternalPayment = combine(
    entityStore,
    $isExternallyPaid,
    (entity, isExternallyPaid) =>
      isExternallyPaid(entity ?? undefined) && !tvShow
  )

  sample({
    clock: openPlayerPaymentPopup,
    source: {
      entity: entityStore,
      externalPayment: $isExternalPayment,
    },
    filter: ({ entity, externalPayment }) => aye(entity) && !externalPayment,
    fn: ({ entity }) => entity!,
    target: paymentEvent,
  })

  sample({
    clock: openPlayerPaymentPopup,
    source: {
      entity: entityStore,
      externalPayment: $isExternalPayment,
    },
    filter: ({ entity, externalPayment }) => aye(entity) && externalPayment,
    fn: ({ entity }) => {
      const result: TryIsPurchased = {
        entity: entity!,
        shouldRefresh,
        mediaType: entity?.mediaType,
      }

      return result
    },
    target: giveItATry,
  })

  const $showPaymentPopup = combine(
    {
      guest: guestModel.$guest,
      entity: entityStore,
      query: routerModel.$query,
    },
    ({ guest, entity, query }) => {
      if (!entity) return null
      const trailer = query.get('trailer')

      return nay(guest) && !entity.free && !entity.purchaseInfo && !trailer
    }
  )

  const $paymentButtonText = combine(
    $paymentButtonTranslation,
    entityStore,
    (paymentButtonTranslation, entity) => paymentButtonTranslation(entity)
  )

  return {
    $paymentButtonText,
    $showPaymentPopup,
    openPlayerPaymentPopup,
  }
}
